import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  FormLabel,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from "@chakra-ui/react";
import { EnumOptionsType, RJSFSchema, WidgetProps } from "@rjsf/utils";
import type { JSONSchema7Type } from "json-schema";
import { useMemo } from "react";

type Option = JSONSchema7Type | EnumOptionsType<RJSFSchema> | undefined;

const optionIsObject = (option: Option): option is EnumOptionsType<RJSFSchema> =>
  !!option && typeof option === "object" && !Array.isArray(option);

export function SelectWidget<T>(props: WidgetProps<T>) {
  const { schema, value, onChange, errorSchema, uiSchema, options, label, readonly, required } =
    props;

  // Add undefined to enum options if not required to allow deselecting
  const enumOptions = useMemo(() => {
    const rawEnumOptions = schema.enum || options?.enumOptions;
    if (!rawEnumOptions) return undefined;
    if (!required) {
      return [undefined, ...rawEnumOptions];
    }
    return rawEnumOptions;
  }, [schema.enum, options?.enumOptions, required]);

  // Add "---" to enum names if not required to allow deselecting and to make it clear to users that they can deselect
  const enumNames = useMemo(() => {
    const rawEnumNames = uiSchema?.["ui:enumNames"] || options?.enumNames;

    if (!rawEnumNames) return undefined;
    if (!required) {
      return ["---", ...rawEnumNames];
    }
    return rawEnumNames;
  }, [uiSchema, options?.enumNames, required]);

  const hasError = errorSchema?.__errors && errorSchema.__errors.length > 0;

  if (!enumOptions) {
    console.warn("SelectWidget: No enum options found");
    return null;
  }

  const getValueLabelDisabled = (option: Option) => {
    const value = optionIsObject(option) ? option?.value : option;
    const optionMatchIndex = enumOptions.findIndex(
      (opt) => (optionIsObject(opt) && opt.value === value) || opt === value
    );

    const optionMatch = enumOptions[optionMatchIndex];
    if (!(optionMatchIndex in enumOptions)) {
      console.warn("SelectWidget: Option not found in enum options", { option, enumOptions });
    }

    const label =
      enumNames?.[optionMatchIndex] ||
      (optionIsObject(optionMatch) ? optionMatch?.label : optionMatch?.toString());

    const isDisabled = !!uiSchema?.["ui:enumDisabled"]?.includes(value);
    return { value, label, isDisabled };
  };

  // Get default value from UI schema or schema or fallback to first option
  const defaultValue =
    schema.default ||
    uiSchema?.["ui:options"]?.default ||
    getValueLabelDisabled(enumOptions[0]).value;

  // Set default value if value is undefined
  if (value === undefined && defaultValue !== undefined && required) {
    onChange(defaultValue);
  }

  return (
    <Box>
      <FormLabel>{label}</FormLabel>
      <Menu isLazy>
        <MenuButton
          type="button"
          h="input"
          w="full"
          borderWidth="1px"
          rounded="md"
          borderColor={hasError ? "red" : "input"}
          pr={1}
          pl={4}
          {...(readonly ? { bg: "gray.100", cursor: "not-allowed ", pointerEvents: "none" } : {})}
        >
          <Flex alignItems="center" justifyContent="space-between">
            <Box>{getValueLabelDisabled(value === undefined ? defaultValue : value)?.label}</Box>{" "}
            <ChevronDownIcon mt={1} />
          </Flex>
        </MenuButton>
        <MenuList zIndex={99999}>
          <MenuOptionGroup value={value}>
            {enumOptions.map((option) => {
              const { value, label, isDisabled } = getValueLabelDisabled(option);
              return (
                <MenuItemOption
                  _disabled={{
                    bg: "rgba(226,232,240, 0.3)",
                  }}
                  onClick={() => onChange(value)}
                  value={value}
                  isDisabled={isDisabled}
                  key={value + label + ""}
                >
                  {label}
                </MenuItemOption>
              );
            })}
          </MenuOptionGroup>
        </MenuList>
      </Menu>
    </Box>
  );
}
